import { z } from 'zod';
import { calendarDateSchema } from './arrangement.schema';
import { createPaginationSchema, objectIdSchema } from './common.schema';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  THE AUDIT LOG — deliberately small, and deliberately COMPLETE.
 *
 *  P1b needs somewhere to record a place merge, because a merge rewrites what a name
 *  resolves to across the whole commissionerate and "who did that, and when" has to
 *  survive it. There was nowhere: the only audit collection in the repo lives inside
 *  `_template/` and is excluded from production builds.
 *
 *  So this ships with ONE writer and ONE reader rather than as a stub. An audit log with
 *  no read path is not an audit log — it is a collection of officer identifiers nobody
 *  can search, which is strictly worse than not having one. The full audit UI is P7;
 *  this is the durable record it will read.
 *
 *  ⚠️ [amended AUDIT-FLAG] AND THE READ PATH IS NOW REACHABLE BY NOBODY UNTIL SOMEBODY IS
 *  GRANTED. `GET /audit` takes `AUDIT_ACCESS` — SUPER_ADMIN **and** the officer's own
 *  `access_analytics` flag — whose only writer is a console, so on a fresh deployment the
 *  paragraph above is true of the CODE and false of the DATABASE: the reader exists and no
 *  officer can use it until `pnpm --filter @pams/api seed:officer-flag` runs. Grant more than
 *  one officer (`docs/ops/analytics.md` §5.1).
 * ─────────────────────────────────────────────────────────────────────────────────────
 */

/**
 * [P2] `POLICY_VERSION` joins `PLACE` on the same argument that admitted `PLACE`: an
 * irreversible, commissionerate-wide state change that later records depend on and that
 * cannot be undone by editing a field back. PA_REQUIREMENTS §3's second principle —
 * "snapshot on publish" — makes publication the exact moment worth recording.
 *
 * ⚠️ WHAT DOES NOT GO IN AN AUDIT CONTEXT: a shape summary. See `policy.service.ts`.
 *
 * [P3a] `ARRANGEMENT` joins them on the same argument again. Publishing a PA issues every
 * duty under it and freezes the matrix; deleting one withdraws an order divisions have
 * already been asked to fill. Both are commissionerate-wide, both are what somebody will
 * later need attributed, and FR-5.2.15 requires the audit row to SURVIVE the thirty-day
 * purge of the arrangement it describes — which is only possible because this collection
 * is separate from the record and carries no TTL.
 */
/**
 * [amended SEED] `ORG_UNIT` joins them, and on a narrower argument than the others.
 *
 * Creating an org unit through `POST /org-units` is NOT audited and does not become
 * audited by this entry — that is an ordinary, reversible, one-node edit, and an audit
 * collection that fills with those stops being the thing anyone reads. What is audited is
 * `pnpm seed:establishment`: eighty-nine nodes written from a file, in one transaction,
 * by a CLI run outside the request cycle. It is the only write in the system that builds
 * the tree FR-3.6's isolation is computed from, it is the one nobody will be able to
 * attribute from an HTTP log because there is no request, and "who imported the
 * establishment, when, and from which version of the file" is exactly the question an
 * audit trail exists to answer.
 *
 * ⚠️ [amended C1d §4g] IT CARRIES TWO CLI EVENTS NOW, NOT ONE, and the paragraph above said
 * "what is audited is `pnpm seed:establishment`" as though it were the only one.
 * `ARRANGEMENTS_RESET` joins it on the same ground — a request-less act nobody could attribute
 * from an HTTP log — and on both the entity is the ROOT unit rather than a node, because the
 * act is commissionerate-wide rather than about one place in the tree.
 *
 * ─── ⚠️ [amended ANALYTICS §8] THE FIRST PARAGRAPH'S REFUSAL IS REVERSED ─────────────────
 *
 * `POST /org-units` IS audited now (`ORG_UNIT_CREATED`), and so are the edit and the move. The
 * refusal above rested on "an ordinary, reversible, one-node edit" — and it was written before
 * `DASHBOARD_LAYOUT` stated the test this vocabulary has been admitted on ever since: **every
 * act this header refuses leaves its own attributable trace on the record it changed.** An org
 * unit does not. It carries no `created_by`, no `updated_by` and no previous value, so after a
 * rename nothing anywhere says what the node was called or who renamed it; and a REPARENT
 * rewrites `ancestors` — the structure FR-3.6's isolation is computed from — with no actor at all.
 *
 * ⚠️ THE VOLUME ARGUMENT STILL HOLDS, WHICH IS WHAT MAKES THE REVERSAL AFFORDABLE: one row per
 * deliberate act by a SUPER_ADMIN or NODAL_ADMIN on a tree of a few thousand nodes, with an edit
 * that changes nothing writing nothing. Not "a row per request". And the reader this header's
 * very first paragraph says an audit log needs exists at last — the Audit log page, which
 * [amended AUDIT-FLAG] a super admin reaches once granted `access_analytics`.
 *
 * ⚠️ THE CLI ROWS ARE UNTOUCHED. `ORG_UNIT_IMPORTED` and `ORG_UNIT_IMPORT_UPDATED` keep their own
 * names, so "did the import do this, or a person?" stays an indexed read on `action`.
 */
export const AUDIT_ENTITY_TYPES = [
  /*
   * ⚠️ [C3a §2] ADMITTED ON `ARRANGEMENT_ATTACHMENT_REMOVED`'s OWN CRITERION, UNAMENDED.
   *
   * An UPLOAD is not audited: the row it creates carries `uploaded_by_officer_id` and
   * `createdAt`, so it leaves its own attributable trace. A REMOVAL destroys that trace, and the
   * audit row is then the only remaining record that the file existed — which is the same
   * sentence, one collection over.
   */
  'UPLOADED_ORDER',
  'PLACE',
  'POLICY_VERSION',
  'ARRANGEMENT',
  'STANDING_ORDER',
  'DEPLOYMENT',
  'ORG_UNIT',
  /*
   * [LIFECYCLE] ⚠️ ADMITTED FOR ONE REASON ONLY: ARCHIVING ONE OF THESE DESTROYS DATA.
   *
   * A post type or a vehicle type is global configuration, and a configuration toggle is
   * exactly what the paragraph above refuses to audit. What earns these two a place is the
   * CASCADE: archiving a master strips its column out of every DRAFT arrangement using it,
   * and the numbers an operator had already typed into that column are gone. A restore
   * cannot bring them back, so the audit row is the only remaining record that they
   * existed and how many there were.
   *
   * ⚠️ AND THAT IS WHY `POLICY` IS ABSENT. Archiving a policy strips nothing, changes no
   * other document and is reversible in one click — the `ARRANGEMENT_GROUP` argument
   * verbatim.
   *
   * ⚠️ [amended ANALYTICS §8] THE SENTENCE ABOVE IS NOW HALF TRUE: `POLICY` IS PRESENT, and the
   * post type's CREATE and EDIT are audited too. The archive argument is untouched — archiving,
   * restoring and setting the default still write nothing, because each leaves `archived_at`,
   * `archived_by_officer_id` or `is_default` on the record itself. What changed is that a create
   * and an edit were never that case: a policy and a post type carry no `created_by` and no
   * previous value, so an edit overwrites the only copy of what the name, the eligible ranks or
   * the description said. That is `DASHBOARD_LAYOUT`'s criterion, applied without amendment.
   */
  'POST_TYPE',
  'VEHICLE_TYPE',
  /*
   * ⚠️ [ANALYTICS §8] THE POLICY ITSELF, beside `POLICY_VERSION` rather than instead of it.
   *
   * `POLICY_VERSION_PUBLISHED` stays on the version: a publish freezes ONE version for ever and is
   * a fact about it. A create, a rename and opening or editing a draft are facts about the POLICY,
   * so they key on the policy's id and carry the version in the context — which makes "what has
   * happened to this policy?" one indexed read on `(entity_type, entity_id, createdAt)` rather
   * than a join across every version it has ever had.
   */
  'POLICY',
  /*
   * ⚠️ [P5a §5c] ADMITTED FOR ONE REASON: A DISCLOSURE HAPPENED, AND NOTHING ELSE RECORDS IT.
   *
   * The paragraph above refuses to audit configuration toggles, and it should — an audit
   * collection that fills with cosmetic events stops being the thing anyone reads. This is
   * not that. The legacy audit found an UNAUTHENTICATED endpoint handing out serving
   * officers' names, emails and mobile numbers, and the officer surface hands out a mobile
   * number by design. The row IS the control's evidence: who asked for whose number, on
   * which duty, from where.
   *
   * ⚠️ THE ENTITY IS THE SUBJECT, NOT THE CALLER. So "who has looked this officer up?" is
   * served by the existing `(entity_type, entity_id, createdAt)` index, and the caller is the
   * actor, which is where every other row in this collection puts the person acting.
   *
   * ⚠️ AND THE VOLUME IS BOUNDED BY THE LIMITER RATHER THAN BY A TTL. `audit_logs` carries no
   * TTL deliberately, so these rows are kept for good; what makes that statable rather than
   * open-ended is `PHONE_REVEAL_MAX_PER_WINDOW`. A disclosure log that expires is not a
   * disclosure log, which is why a separate TTL'd collection was considered and rejected.
   */
  'OFFICER',
  /*
   * ⚠️ [P6b] ADMITTED FOR ONE REASON: EVERY SAVE OVERWRITES THE ONLY COPY, AND THE SURFACE IT
   * CHANGES BELONGS TO SOMEBODY WHO CANNOT SEE THAT IT CHANGED.
   *
   * The paragraph above refuses to audit configuration and it is still right to. What
   * separates this from a configuration toggle is NOT that it is important — "it changes what
   * other people see" is equally true of archiving a post type, and that one was admitted for
   * a different reason. It is that **every toggle this header refuses leaves its own
   * attributable trace on the record it changed.** Archiving a policy writes `archived_at`,
   * `archived_by_officer_id` and `archived_from_status`: the record itself says what it was,
   * and one click puts it back. A `dashboard_layouts` row carries `updated_by_officer_id` and
   * `updatedAt` — WHO and WHEN, and never WHAT — and the next save overwrites them. That is
   * `POST_TYPE`'s admission applied without amendment: the audit row is the only remaining
   * record of what the action destroyed, which here is the previous arrangement and the
   * operator's own typed words.
   *
   * ⚠️ AND THE PERSON WHOSE SCREEN CHANGED CANNOT DIFF IT. A layout is keyed on
   * `(accessRole, orgUnitType)`; the nodal whose red-count tile vanished holds neither
   * `audit.read` nor any view of this collection, and their dashboard renders the new
   * arrangement as though it had always been so. There is nowhere else in this system where
   * "why did my tile disappear" is answerable at all.
   *
   * ⚠️ UNLIKE [PRE-P6.2]'s DUTY HISTORY, IT NEEDS NO NEW READER. `audit.read` is SUPER_ADMIN
   * only and so is `dashboard.configure` — the people who write these rows are exactly the
   * people who can read them back, with no capability widened and no narrow reader minted.
   * ⚠️ [amended AUDIT-FLAG] "EXACTLY THE PEOPLE" IS NOW "A SUPERSET OF THEM", AND THAT IS STATED
   * RATHER THAN QUIETLY LEFT. Reading the log takes `AUDIT_ACCESS` — the role AND the officer's
   * own `access_analytics` flag — while `dashboard.configure` takes the role alone, so a super
   * admin who has not been granted the flag can rewrite every nodal's dashboard and cannot read
   * back the only surviving record of what their save destroyed. Still no new reader and no
   * capability widened: the row is written, attributable and unreadable BY ITS OWN AUTHOR until
   * somebody is granted (`docs/ops/analytics.md` §5.1). If that is the wrong trade, the answer is
   * a SECOND flag, not a second reader.
   *
   * ⚠️ AND THE VOLUME IS ONE ROW PER DELIBERATE ACT ON A TWELVE-ROW COLLECTION, with a save
   * that changes nothing writing nothing at all. This is not "a row per request", so the TTL
   * question this header opens stays closed.
   */
  'DASHBOARD_LAYOUT',
  /*
   * ⚠️ [C1h] ADMITTED ON `DASHBOARD_LAYOUT`'s CRITERION, APPLIED WITHOUT AMENDMENT.
   *
   * This header refuses to audit configuration five times over and it is still right to. What
   * separates this is the criterion above: **every toggle this header refuses leaves its own
   * attributable trace on the record it changed, and this one does not.** An `app_settings`
   * row carries `updated_by_officer_id` and `updatedAt` — WHO and WHEN, and never WHAT — and
   * the next save overwrites both. Which value it held before is nowhere on the record.
   *
   * ⚠️ AND THE PEOPLE WHOSE PRODUCT CHANGED CANNOT DIFF IT — the layout argument, one step
   * wider. The UI mode is SYSTEM-WIDE (§1d: there is no personal override), so every operator
   * in the commissionerate opens a differently-coloured product one morning, and not one of
   * them holds `settings.write` or any view of this collection. "Why does it look like this"
   * is otherwise unanswerable.
   *
   * ⚠️ NO NEW READER IS NEEDED. `audit.read` is SUPER_ADMIN only and so is `settings.write` —
   * the people who write these rows are exactly the people who can read them back, with no
   * capability widened and no narrow reader minted. `DASHBOARD_LAYOUT`'s note verbatim —
   * ⚠️ [amended AUDIT-FLAG] INCLUDING ITS CORRECTION: reading now also needs `access_analytics`,
   * so the writers are a SUPERSET of the readers and an ungranted super admin repaints the whole
   * product without being able to read the row that says they did.
   *
   * ⚠️ AND THE VOLUME IS ONE ROW PER DELIBERATE ACT ON A ONE-ROW COLLECTION, with a save that
   * changes nothing writing nothing at all. This is not "a row per request", so the TTL
   * question this header opens stays closed — which is precisely the test [C1h]'s login
   * telemetry FAILED, and why that went to its own collection instead of here.
   */
  'SETTING',
  /*
   * ⚠️ [P7B §2] THE TEMPLATE SET AND ITS TEMPLATES, AND THE APPROVED RUN THAT CREATES OR PUBLISHES FROM
   * THEM. A template is READ-ONLY FOR EVER — its one writer is the import, which creates and never
   * updates — so a template row records an import, never an edit. A decision on a code is filed under
   * the SET (it applies to every template using the code), and a run under its own id: its items are
   * overwritten as the run advances, so the approval row is the record of what was approved.
   */
  'TEMPLATE_SET',
  'TEMPLATE',
  'TEMPLATE_RUN',
] as const;
export type AuditEntityType = (typeof AUDIT_ENTITY_TYPES)[number];

export const AUDIT_ACTIONS = [
  'PLACE_MERGED',
  'POLICY_VERSION_PUBLISHED',
  'ARRANGEMENT_PUBLISHED',
  'ARRANGEMENT_RESTAMPED',
  'ARRANGEMENT_ARCHIVED',
  'ARRANGEMENT_RESTORED',
  /*
   * [D4.4 §5] ⚠️ `PUBLISHED → DRAFT`, AND IT IS A SEPARATE ACTION FROM `ARRANGEMENT_ARCHIVED`
   * ON PURPOSE.
   *
   * The two destroy different things and mean different things: archiving withdraws an
   * order from circulation and keeps everything, un-publishing keeps the order and DESTROYS
   * its duties and every name against them. One action name for both would make "how did
   * this order lose its assignments?" unanswerable from the audit log, which is the one
   * question this row exists to answer. It carries the operator's REASON and the counts
   * discarded, because a week later nobody remembers either.
   */
  'ARRANGEMENT_UNPUBLISHED',
  /*
   * ─── ⚠️ [C1b §4d] A CROSS-DIVISION GRANT OF SIGHT, AND WHY IT IS AUDITED AT ALL ──
   *
   * This header refuses to audit CONFIGURATION three times over, and it is still right to.
   * "It changes what other people see" does not discriminate — that is equally true of
   * archiving a post type. What does is the criterion `DASHBOARD_LAYOUT` was admitted on:
   * every toggle this header refuses leaves its own attributable trace on the record it
   * changed, and this one does not. `associated_org_unit_ids` is OVERWRITTEN by the next
   * save; there is no `removed_at`, no actor, no restore. The audit row is the only
   * remaining record of what the act destroyed.
   *
   * And what it destroys is not a preference. FR-3.6's isolation is the one rule this system
   * is built around, and an association is the single per-arrangement, creator-chosen
   * exception to it — so both directions of the change are worth a row: `added` is a
   * boundary being crossed, `removed` is sight being taken away from a unit that cannot get
   * it back on its own.
   *
   * ⚠️ ONE ACTION, NOT TWO. A single PATCH can add and remove in the same act, so
   * `..._ASSOCIATED` and `..._DISASSOCIATED` would mean two rows for one act, or an
   * arbitrary choice between them. `ARRANGEMENT_RESTAMPED` is the precedent for one action
   * describing a set change.
   *
   * ⚠️ THE CONTEXT CARRIES SHORT CODES, WHICH IS IDENTITY — `ORG_UNIT_IMPORTED`'s rule — and
   * never counts, which is CLAIM 4's.
   */
  'ARRANGEMENT_ASSOCIATED_UNITS_CHANGED',
  /*
   * [P3b.1] ⚠️ NOTHING IS AUDITED FOR AN ARRANGEMENT GROUP, and nothing for the generator's
   * DRAFT phase. A group changes no status, no allocation and no duty and is reversible in
   * one click; a materialised draft is audited when it PUBLISHES, through
   * `ARRANGEMENT_PUBLISHED`, like any other. An audit collection that fills with cosmetic
   * events stops being the thing anyone reads.
   */
  /*
   * [P4] ⚠️ FULFILMENT IS AUDITED PER SLOT, ON THE DUTY, BECAUSE THAT IS WHERE THE FACT IS.
   *
   * Every fill, swap and completion writes one row (FR-5.3.8) — these are the events an
   * officer's presence on a bandobast is later reconstructed from, so unlike a grouping or a
   * generator's draft they are exactly what an audit collection is for. The entity is the
   * DEPLOYMENT rather than the arrangement: an order runs for a month and "who was named"
   * is a question about one night.
   */
  'ASSIGNMENT_NAMED',
  'ASSIGNMENT_REMOVED',
  'ASSIGNMENT_SWAPPED',
  'ALLOCATION_COMPLETED',
  /**
   * ─── ⚠️ [C3c.1 §6] A NODAL TELLING THE OFFICERS ON A DUTY THAT THEY ARE ON IT ───────
   *
   * ⚠️ IT MEETS `POST_TYPE_ARCHIVED`'s ADMISSION CRITERION AND NEEDS NO NEW ONE. `duty_notifications`
   * carries a TTL, so the ROWS are gone in thirty days and the audit row is the only remaining
   * record that anybody was told at all — which is exactly the "the record itself does not keep
   * it" test this vocabulary is admitted on. `DASHBOARD_LAYOUT`'s admission is the same shape.
   *
   * ⚠️ THE CONTEXT CARRIES A COUNT AND ORG UNIT IDs, AND NO OFFICER. Counts are legal here on
   * `POST_TYPE_ARCHIVED`'s reasoning — they describe what the act did — and naming the thirty
   * recipients would put a roster into a collection with a broader read grant than the endpoint
   * guarding the record it describes (`auditFill`'s standing rule).
   *
   * ⚠️ `_SENT`, NEVER `_DELIVERED`. Nothing here learns whether an officer opened the app;
   * §6e is in-app only and §6f forbids wiring SMS. `OFFICER_CALL_INITIATED`'s rule: an audit
   * trail that overstates by one word is one a reader stops trusting.
   */
  'DUTY_NOTIFICATION_SENT',
  /*
   * ─── ⚠️ [C5c §5g] A COMMANDER MARKING ANOTHER OFFICER'S ATTENDANCE, AND CORRECTING IT ──────
   *
   * ADMITTED ON THE BRIEF'S OWN GROUND AND ON THE HEADER'S TEST TOGETHER. §5g: an absence ENDS an
   * officer's duty record by one person's entry and nothing in the app can appeal it, so the brief
   * asks for both the marking and any correction to be audited with the commander's identity. And
   * the record alone does not keep what these rows keep: `pnpm reset:arrangements` deletes
   * `duty_events` outright while audit rows survive it, and a correction VOIDS a marking — the row
   * still says it was withdrawn, but not by whom the replacement was asked for or what it replaced.
   *
   * ⚠️ THREE ACTIONS RATHER THAN ONE WITH A `type` KEY — the `OFFICER_IMPORTED` precedent. "Which
   * absences did commanders record?" is the question an inquiry asks first, and a name makes it an
   * indexed read on `action` rather than a context scan. The on-behalf PRESENCE markings share one
   * name (`_MARKED_FOR_OFFICER`) and the context names the type, because "marked present" would
   * overstate a Stand Down by a word — `OFFICER_CALL_INITIATED`'s rule.
   *
   * ⚠️ THE ENTITY IS THE `DEPLOYMENT` — the duty the marking is about — so "who marked whom on this
   * duty" is one read on `(entity_type, entity_id, createdAt)`. `listDutyHistory` filters on its OWN
   * actions (`DUTY_HISTORY_ACTIONS`), so these do not reach the fill screen's history drawer.
   *
   * ⚠️ IDS AND CODES ONLY IN THE CONTEXT — `dutyEventId`, `subjectOfficerId`, the event `type`, the
   * stop for a Mark Reached, and the absence reason ONLY when it is one of `ABSENCE_REASONS`. Free
   * text is never copied: `audit_logs` has a broader read grant than the duty, and no TTL.
   *
   * ⚠️ ONE ROW PER ACT, never one for the row the server derived from it, and a replay writes none.
   * Volume is one row per commander-entered marking — the dead-phone path, not a row per request.
   */
  'ATTENDANCE_ABSENCE_RECORDED',
  'ATTENDANCE_MARKED_FOR_OFFICER',
  /*
   * ⚠️ [C5c §5f] THE CORRECTION'S OWN ROW. Its context names the marking withdrawn
   * (`dutyEventId`, `fromType`), what the commander asked for (`to`) and the marking that replaced
   * it (`replacementEventId`, absent when nothing did) — the pair a later reader needs to say
   * "absent, then present, by the same Inspector, twenty minutes later".
   */
  'ATTENDANCE_MARKING_CORRECTED',
  /*
   * ─── ⚠️ [C1b §3] A DIVISION GIVING PART OF ITS SHARE TO ONE OF ITS OWN UNITS ─────
   *
   * On the ARRANGEMENT rather than the deployment, because the act is ORDER-GRAINED: one
   * requirement line is stamped onto every night of a duty point, so a row per re-stamped
   * night would be fourteen rows for one decision. `ARRANGEMENT_RESTAMPED` is the precedent.
   *
   * ⚠️ IT MEETS `POST_TYPE_ARCHIVED`'s ADMISSION CRITERION RATHER THAN NEEDING A NEW ONE.
   * The record itself keeps `delegated_from_alloc_key` — WHAT, never WHO or WHEN — and the
   * next delegation of the same source overwrites the counts. The audit row is the only
   * remaining record of what the act destroyed, and what it destroys is a unit's sight of an
   * order as well as its share of the work.
   *
   * ⚠️ THE CONTEXT CARRIES SHORT CODES AND COUNTS OF WHAT CHANGED, AND NO OFFICER. Counts are
   * legal here on `POST_TYPE_ARCHIVED`'s reasoning — they describe what the act changed and
   * destroyed — and not on `POLICY_VERSION_PUBLISHED`'s, which is a shape summary CLAIM 4
   * forbids.
   *
   * ⚠️ AND `listDutyHistory` IS NOT WIDENED, WHICH IS A DECISION RATHER THAN AN OMISSION. Its
   * filter is `entity_type: 'DEPLOYMENT'` and its DTO's `before`/`after` are officer
   * summaries; admitting an order-grained, org-unit-shaped event needs either a second
   * payload shape or two nullable columns nothing else reads. If the client asks for "who
   * delegated this and when", it is one `$or` arm on `(entity_type, entity_id, createdAt)`
   * plus those two columns — written down here so the cost is known rather than rediscovered.
   */
  'ALLOCATION_DELEGATED',
  /*
   * ─── ⚠️ [C6a S1b] A DIVISION ANSWERING A COMMAND POSITION IT WAS ASKED FOR ─────────
   *
   * On the ARRANGEMENT, for `ALLOCATION_DELEGATED`'s reason: a seat is a DUTY-POINT position and
   * one name reaches every duty of that point still open for naming, so a row per duty would be a
   * row per night for one decision.
   *
   * ⚠️ ADMITTED ON `POST_TYPE_ARCHIVED`'s CRITERION. The seat keeps WHO holds it and nothing about
   * who put them there or when; a hand-down is a delete-and-insert that leaves no trace of where
   * the position was before. The audit row is the only record of either.
   *
   * ⚠️ IDS AND CODES ONLY — the order, the duty point, the seat and its ask, the role's key, the
   * division and unit ids, and the officer id as `before`/`after` (the page names it at read time,
   * `auditLogNames.service.ts`). Never a name and never a phone ([C1g §2e]'s rule for a row with a
   * broader read grant than the record it describes), and never a number: a command position is
   * not a figure (FR-5.2.6b).
   */
  'COMMAND_SEAT_HANDED_DOWN',
  'COMMAND_SEAT_NAMED',
  'COMMAND_SEAT_CLEARED',
  'STANDING_ORDER_CREATED',
  'STANDING_ORDER_UPDATED',
  'STANDING_ORDER_ENDED',
  /*
   * [amended SEED] ⚠️ ONE ROW PER NODE THE IMPORT ACTUALLY CREATED OR CHANGED, AND NONE
   * FOR A NODE IT LEFT ALONE.
   *
   * That is not a rounding of "one row per run" — it is what makes the seeder's own exit
   * criterion true. `audit_logs` carries no TTL and only ever grows, so a row per node per
   * RUN would add 89 rows every time the deploy step is re-run and "three consecutive runs
   * leave the database identical" would be false in a collection nobody thought to check.
   * Skipping the unchanged nodes makes runs two and three write nothing at all, here as
   * well as in `org_units`.
   *
   * The context carries IDENTITY, never shape: the short code, the section code, the file
   * version and the file's `source` line. No counts — a total is a property of the run, not
   * of the node the row is about, and `commandRoleIsolation.test.ts` CLAIM 4 is the standing
   * argument for keeping shape out of an audit context.
   */
  'ORG_UNIT_IMPORTED',
  'ORG_UNIT_IMPORT_UPDATED',
  /*
   * [LIFECYCLE] ⚠️ THE CONTEXT ON THESE TWO CARRIES COUNTS, WHICH CLAIM 4 FORBIDS FOR A
   * PUBLISH — AND THE DISTINCTION IS THE WHOLE JUSTIFICATION.
   *
   * `POLICY_VERSION_PUBLISHED` may not carry a column count because that is a SHAPE
   * SUMMARY: the shape is still there afterwards, readable from the document itself, and a
   * count in the log is a second copy that can disagree with it. `POST_TYPE_ARCHIVED`
   * carries `draftsStripped` and `typedCountLost` because those describe what the action
   * DESTROYED — the one thing that is not readable from any document afterwards, and the
   * one thing a restore cannot give back. Never a column count, never a command-role count.
   */
  'POST_TYPE_ARCHIVED',
  'POST_TYPE_RESTORED',
  'VEHICLE_TYPE_ARCHIVED',
  'VEHICLE_TYPE_RESTORED',
  /*
   * [LIFECYCLE] Archiving a standing order takes it off the working list; it stops no
   * generation (ending already did that) and touches nothing it generated. It is audited on
   * the arrangement's own argument: withdrawing an order divisions have already been asked
   * to fill is not a cosmetic event.
   */
  'STANDING_ORDER_ARCHIVED',
  'STANDING_ORDER_RESTORED',
  /*
   * [LIFECYCLE] ⚠️ ONE ACTION FOR THE MANUAL HARD DELETE AND FOR P7's SWEEP, because they
   * have one outcome. Two actions for one outcome is how an audit read path stops being
   * trustworthy — the reader has to know both names or silently miss half the answer.
   */
  'ARRANGEMENT_PURGED',
  /*
   * ⚠️ [P5a §5c] ONE ROW PER FORCE PHONE NUMBER DISCLOSED — see `OFFICER` above for why this
   * is not a cosmetic event.
   *
   * Its context carries `deploymentId` and `ip`. The IP is stored RAW rather than hashed as
   * the OTP limiter stores it, and that is the difference between the two: a limiter needs
   * only to tell addresses apart, while a disclosure record you cannot resolve to a device is
   * not a record of anything.
   *
   * ⚠️ NO NAME AND NO NUMBER IN THE CONTEXT. Identity and ids, never PII — `auditFill`'s rule,
   * and sharper here: writing the number into the log would put it somewhere with a broader
   * read grant than the endpoint that guards it.
   */
  'OFFICER_PHONE_REVEALED',
  /*
   * [amended INGEST] ⚠️ ONE ROW PER OFFICER THE IMPORT ACTUALLY CREATED OR CHANGED, AND
   * NONE FOR ONE IT LEFT ALONE — `ORG_UNIT_IMPORTED`'s rule, for the same reason.
   *
   * `audit_logs` carries no TTL and only ever grows, so a row per officer per RUN would add
   * twelve thousand rows every time the deploy step is re-run, and "three consecutive
   * commits leave the database identical" would be false in a collection nobody thought to
   * check. Skipping the unchanged rows makes runs two and three write nothing at all, here
   * as well as in `officers`.
   *
   * ⚠️ TWO ACTIONS RATHER THAN ONE, matching `ORG_UNIT_IMPORTED` / `ORG_UNIT_IMPORT_UPDATED`
   * exactly. "Was this officer created by the import, or did the import move them?" is the
   * question anybody auditing a posting change asks first, and one action name for both
   * makes it unanswerable without joining to `posting_history`.
   *
   * ⚠️ THE CONTEXT CARRIES PROVENANCE AND NO PII. `OFFICER_PHONE_REVEALED` above sets the
   * rule for this entity type — no name and no number, because `audit_logs` has a broader
   * read grant than the officer record it describes. What goes in is the source file, the
   * line, the source's own ids and what the two resolved columns resolved TO, which is
   * exactly what "who imported this officer, from where, and how was their posting decided"
   * needs.
   */
  'OFFICER_IMPORTED',
  'OFFICER_IMPORT_UPDATED',
  /*
   * ─── ⚠️ [C1g §2e] THE DIRECTORY'S TWO ROWS, ADMITTED ON `OFFICER_PHONE_REVEALED`'s ────
   *      CRITERION APPLIED WITHOUT AMENDMENT: this is the same data class.
   *
   * The header above refuses to audit CONFIGURATION three times over, and that refusal is
   * untouched — these are DISCLOSURES of serving officers' contact details, which is exactly
   * what the legacy audit found being handed out unauthenticated.
   *
   * ⚠️ ONE ROW PER **LIST VIEW**, NOT PER OFFICER, AND THE ARITHMETIC IS WHY.
   *
   * §2d renders phone numbers INLINE, so a naive per-render row would write one audit entry
   * per name on screen — and the largest single org unit in the live establishment holds
   * **2 441 officers**. That is not the question an investigation asks: "who opened this
   * unit's roster" is one act, and 2 441 rows describing it is a log that has to be
   * de-duplicated before it can be read. The row therefore names the UNIT and the COUNT.
   *
   * ⚠️ THE ENTITY IS THE `ORG_UNIT`, not an officer — the unit is what was opened, and it is
   * what `(entity_type, entity_id, createdAt)` then answers "who has been reading this
   * station's roster?" for, on an index that already exists.
   *
   * ⚠️ AND IT IS PER REQUEST, SO PAGING WRITES ONE PER PAGE. Stated rather than discovered:
   * an operator scrolling a large unit writes a row per page of 25, which is still a record of
   * what was actually disclosed rather than of what could have been.
   */
  'OFFICER_DIRECTORY_VIEWED',
  /*
   * ─── ⚠️ [C5 §7b] ONE ROW PER PRINTED ORDER SHEET OPENED ──────────────────────────────────
   *
   * ADMITTED ON THE GROUND `OFFICER_PHONE_REVEALED`, `OFFICER_DIRECTORY_VIEWED` AND
   * `OFFICER_TRAIL_VIEWED` ALL STAND ON: a DISCLOSURE happened and nothing else records it.
   * §7b puts every named officer's phone number on a document whose entire purpose is to be
   * printed and left on a desk — reversing a control `orderSheetOfficerSchema` held three ways
   * — and this row is what replaces the per-officer meter that guards every other disclosure
   * in the product.
   *
   * ⚠️ ONE ROW PER REQUEST, NOT PER OFFICER. `MAX_ORDER_SHEET_ASSIGNMENTS` is 20 000, so a row
   * per name would be a log nobody can read; "who printed this order" is one act, which is
   * `OFFICER_DIRECTORY_VIEWED`'s own arithmetic. A reload writes a second row, and that is a
   * record of what was actually disclosed rather than of what could have been.
   *
   * ⚠️ THE ENTITY IS THE `ARRANGEMENT`, so `(entity_type, entity_id, createdAt)` answers "who
   * has been printing this order?" on an index that already exists.
   *
   * ⚠️ AND THE CONTEXT CARRIES A COUNT AND NOTHING ELSE. `OFFICER_PHONE_REVEALED` sets that
   * rule for this data class and the reason is unchanged: `audit_logs` has a broader read grant
   * (SUPER_ADMIN, whole-log) than the endpoint guarding the record it describes, so naming the
   * officers here would put a roster — with numbers — into the one collection that has no TTL.
   *
   * ⚠️ `_VIEWED`, NEVER `_PRINTED`. Nothing here learns whether the operator reached a printer;
   * what is observed is that the sheet was served. `OFFICER_CALL_INITIATED`'s rule, that a
   * trail overstating by one word is one a reader stops trusting.
   */
  'ORDER_SHEET_VIEWED',
  /*
   * ⚠️ THE ACT THAT MATTERS (§2e: "the second is the event that matters"), AND IT IS AN
   * INTENT RATHER THAN A CALL.
   *
   * Nothing here knows whether a call connected — the client hands the number to the handset
   * and the handset leaves. `_INITIATED` says what was actually observed; `_CALLED` would be a
   * claim this system cannot support, and an audit trail that overstates by one word is one a
   * reader stops trusting.
   *
   * ⚠️ THE ENTITY IS THE `OFFICER` WHO WAS RUNG — the mirror of the row above, and what makes
   * "who has been contacting this officer?" a single indexed read.
   *
   * ⚠️ NO NAME AND NO NUMBER IN THE CONTEXT. `OFFICER_PHONE_REVEALED` sets that rule for this
   * entity type and the reason is unchanged: `audit_logs` has a broader read grant
   * (SUPER_ADMIN, whole-log) than the endpoint that guards the record it describes.
   */
  'OFFICER_CALL_INITIATED',
  /*
   * ─── ⚠️ [ANALYTICS §6] ONE ROW PER OFFICER TRAIL OPENED ─────────────────────────────────
   *
   * ADMITTED ON THE GROUND `OFFICER_PHONE_REVEALED` AND `OFFICER_DIRECTORY_VIEWED` STAND ON: a
   * DISCLOSURE happened and nothing else records it. An officer's last fifteen addresses and last
   * fifteen locations are read from columns no other endpoint selects, on one page, by a
   * SUPER_ADMIN — and a read leaves no trace on the record it read.
   *
   * ⚠️ THE CONTEXT IS EMPTY, AND THAT IS THE RULE RATHER THAN A GAP. The viewer is
   * `actor_officer_id`, the subject is `entity_id`, and the instant is `createdAt`: that is the
   * whole of "who looked at whose trail, when". No value, no name, no address and no coordinate
   * goes in: the trail endpoint is the one sanctioned reader of those values, and `audit_logs`
   * carries no TTL — a row that copied the trail would be a second trail that outlives
   * `OFFICER_TRAIL_RETENTION_DAYS` for ever.
   */
  'OFFICER_TRAIL_VIEWED',
  /*
   * ─── [ADMIN-UPDATE] THE THREE ACTS OF `pnpm update:officer-admins` ──────────────────
   *
   * ⚠️ ADMITTED ON `DASHBOARD_LAYOUT_CONFIGURED`'s CRITERION, WHICH THIS HEADER STATES AS THE
   * TEST: every act this file refuses to audit leaves its own attributable trace on the record
   * it changed. An officer record carries `updatedAt` and NEVER what it held before, and the
   * next save overwrites even that — so after a bulk identity correction there is nothing
   * anywhere that says the account used to belong to a different post, or who decided so.
   *
   * ⚠️ THREE ACTIONS, NOT ONE WITH A `strategy` KEY. `OFFICER_IMPORTED` /
   * `OFFICER_IMPORT_UPDATED` set the precedent: "was this officer corrected, moved aside, or
   * destroyed?" is the first question anyone auditing this run asks, and one name makes it a
   * context lookup rather than an indexed read.
   *
   * ⚠️ NO NAME, NO NUMBER AND NO ADDRESS IN ANY OF THE THREE CONTEXTS. `OFFICER_PHONE_REVEALED`
   * sets that rule for this entity type and the reason is unchanged — `audit_logs` has a
   * broader read grant than the endpoint guarding the record it describes. What changed is
   * carried as BOOLEANS; what it was before lives in the rollback CSV, which is officer PII and
   * is gitignored as such.
   */

  /**
   * The correction itself, on the account that was corrected. Its context says WHICH fields
   * moved and whether a conflict had to be resolved to make room — never the values.
   */
  'OFFICER_IDENTITY_CORRECTED',
  /**
   * ⚠️ THE REVERSIBLE HALF OF A CONFLICT, on the officer moved aside.
   *
   * Their phone is rewritten so the number is freed, their email is unset so the second unique
   * index is freed too, and they are deactivated. The record itself does not say why any of
   * that happened — `is_active` has no actor and no timestamp of its own — so this row is the
   * only account of it, which is exactly `POST_TYPE_ARCHIVED`'s admission.
   */
  'OFFICER_PHONE_MASKED',
  /**
   * ⚠️ THE IRREVERSIBLE HALF, and it clears the bar outright: the act leaves NO RECORD AT ALL.
   *
   * There is no `deleted_at`, no archive row and no tombstone — after it, nothing in the
   * database says the officer ever existed. `ARRANGEMENTS_RESET`'s criterion, unamended, and
   * `ARRANGEMENT_PURGED` is the sibling name.
   *
   * ⚠️ ITS `entity_id` DANGLES BY DESIGN. That is the point rather than a defect:
   * `(entity_type, entity_id, createdAt)` is still the one query that answers "what happened to
   * this id?" for every other collection that still references it.
   */
  'OFFICER_PURGED',
  /*
   * ⚠️ [P6b] TWO ACTIONS RATHER THAN ONE, on `ORG_UNIT_IMPORTED` / `ORG_UNIT_IMPORT_UPDATED`'s
   * rule. "Did an operator arrange this, or throw it back to the default?" is the first
   * question anyone auditing a layout asks, and one name makes it unanswerable without
   * reconstructing the seeded default from code that has since changed.
   */
  'DASHBOARD_LAYOUT_CONFIGURED',
  'DASHBOARD_LAYOUT_RESET',
  /*
   * ─── ⚠️ [C1d §4g] `pnpm reset:arrangements` — ADMITTED ON `POST_TYPE_ARCHIVED`'s ────
   *      CRITERION, APPLIED WITHOUT AMENDMENT.
   *
   * This header refuses to audit CONFIGURATION four times over and it is still right to.
   * What separates this is not that it is important — "it changes what other people see" is
   * equally true of archiving a post type, and that one was admitted for a different reason.
   * It is the criterion `DASHBOARD_LAYOUT` was admitted on: **every act this header refuses
   * leaves its own attributable trace on the record it changed, and this one leaves NO RECORD
   * AT ALL.** It hard-deletes every arrangement, deployment, allocation fill, assignment, duty
   * event and arrangement group in the database. There is no `deleted_at`, no archive to
   * restore from, no row carrying who ran it, and afterwards nothing anywhere says an order
   * ever existed. `ARRANGEMENT_PURGED` audits ONE such deletion; this is all of them at once,
   * from a CLI outside the request cycle where no HTTP log can attribute it —
   * `ORG_UNIT_IMPORTED`'s second ground.
   *
   * ⚠️ ONE ROW PER RUN, ON THE ROOT ORG UNIT, NOT ONE PER ARRANGEMENT. The act is
   * commissionerate-wide and a per-order row would put thousands into a collection with no TTL
   * while still carrying no total. `entity_id` is the actor's ROOT unit for the same reason
   * `ORG_UNIT_IMPORTED`'s is a node: it is the thing the act was performed on.
   *
   * ⚠️ THE CONTEXT CARRIES COUNTS, WHICH CLAIM 4 FORBIDS FOR A PUBLISH — and the distinction
   * is the whole justification, `POST_TYPE_ARCHIVED`'s note verbatim. These are not a shape
   * summary readable from a document afterwards, because there is no document afterwards. They
   * are what was DESTROYED, and a week later they are the only answer to "how much".
   *
   * ⚠️ AND NO REFERENCES, NO OFFICER IDS AND NO PHONE. `OFFICER_PHONE_REVEALED`'s rule —
   * identity and ids, never PII — and it bites harder here: listing the orders would put every
   * PA the commissionerate ever ran into the one collection this act does not delete.
   *
   * ⚠️ A RUN THAT DESTROYED NOTHING WRITES NOTHING. `ORG_UNIT_IMPORTED`'s rule: this
   * collection has no TTL and only ever grows, so a re-run against an already-cleared database
   * must leave it identical rather than adding a row of zeros.
   */
  'ARRANGEMENTS_RESET',
  /*
   * ⚠️ [C1h] ONE ROW PER CHANGE, CARRYING `from` AND `to`.
   *
   * The context names both values, which makes the row informative rather than merely
   * attributable: with a two-member enum the previous value happens to be recoverable by
   * inspection today, and it stops being so the moment a third mode exists. Recording it now
   * means that release changes nothing here.
   *
   * ⚠️ `entity_id` IS THE SETTINGS ROW, NOT THE ACTOR'S ORG UNIT. Unlike `ARRANGEMENTS_RESET`
   * there IS a document afterwards and it is the thing the act was performed on.
   *
   * ⚠️ AND A SAVE THAT CHANGES NOTHING WRITES NOTHING — not the row, not `updatedAt`, not an
   * audit row. `DASHBOARD_LAYOUT_CONFIGURED`'s rule and `ORG_UNIT_IMPORTED`'s reason: this
   * collection has no TTL and only ever grows.
   */
  'SETTING_CHANGED',
  /*
   * ⚠️ [C2b §3] THE REMOVAL IS AUDITED AND THE UPLOAD IS NOT, AND THE ASYMMETRY IS
   * `DASHBOARD_LAYOUT_CONFIGURED`'s CRITERION APPLIED RATHER THAN AN OVERSIGHT.
   *
   * That header refuses to audit an act which "leaves its own attributable trace on the record
   * it changed". An upload leaves exactly that: the `attachments` row carries
   * `uploaded_by_officer_id` and `createdAt`, so "who put this on the order" is answerable from
   * the thing itself. A REMOVAL destroys that row and the file with it — no `archived_at`, no
   * restore, and afterwards nothing anywhere says the document existed. `ARRANGEMENT_PURGED`'s
   * ground, on a smaller object.
   *
   * ⚠️ THE CONTEXT CARRIES THE DESTROYED FILENAME AND SIZE, which is the only remaining record
   * of what was taken off the order — the same reason `POST_TYPE_ARCHIVED` may carry counts
   * where a publish may not. It carries no content and no uploader name: `audit_logs` has a
   * broader read grant than the endpoint guarding the record it describes.
   *
   * ⚠️ AND A DOWNLOAD IS NOT AUDITED. It is a read of a record the caller can already open —
   * `GET /arrangements/:id` is not audited either — and a row per download would put one entry
   * per page view into a collection with no TTL. `OFFICER_PHONE_REVEALED` is metered and
   * audited because the number is ABSENT from the payload and the disclosure is the act; here
   * the entitlement is the order, and the order is the thing that was already granted.
   */
  'ARRANGEMENT_ATTACHMENT_REMOVED',
  /* ⚠️ [C3a §2] The context carries the DESTROYED title and filename — never the surviving
     ones, which is `DASHBOARD_LAYOUT`'s rule and `POST_TYPE_ARCHIVED`'s before it. */
  'UPLOADED_ORDER_REMOVED',
  /*
   * ─── ⚠️ [ANALYTICS §8] THE FOUR MASTERS THE UI EDITS, AND WHY EACH ACT CLEARS THE BAR ──────
   *
   * Before this pass an org unit, an officer, a post type and a policy could be created, edited
   * and (for the first two) moved from the dashboard with no row anywhere. Each action below is
   * admitted on THIS HEADER'S OWN TEST — the act leaves no attributable trace on the record it
   * changed — and each fails that test in one of three ways:
   *
   *   · an EDIT overwrites the only copy of the old values (none of the four keeps history);
   *   · a MOVE leaves no actor — `posting_history` records WHEN an officer moved and never WHO
   *     moved them, and a reparent rewrites `ancestors` with no column saying who did;
   *   · a CREATE records no creator — none of these models stores `created_by` (a policy
   *     VERSION does; the policy does not).
   *
   * ⚠️ THE CONTEXT IS A DIFF, IN ONE CONVENTION. `changed` lists the fields, `from:`/`to:` hold
   * disclosable values and `added:`/`removed:` hold set changes as CODES — see
   * `constants/auditChange.constant.ts`. A reference is its short code, rank code or short name,
   * NEVER an id: an id in a log is a lookup somebody has to do, and a code is what the operator
   * typed.
   *
   * ⚠️ AN OFFICER'S NAME, PHONE, EMAIL, SERVICE NUMBER AND PHOTO ARE RECORDED AS `changed` ONLY.
   * `OFFICER_PHONE_REVEALED`'s rule for this entity type, unamended: `audit_logs` has a broader
   * read grant than the officer record. `OFFICER_CREATED` therefore carries the posting, the rank
   * and the access role and no identity at all — the entity id is the officer.
   *
   * ⚠️ A SAVE THAT CHANGES NOTHING WRITES NOTHING — no document write, no `updatedAt`, no row.
   * `SETTING_CHANGED`'s rule and `ORG_UNIT_IMPORTED`'s reason: this collection has no TTL and only
   * ever grows. It is a behaviour change for an identical PATCH, which used to bump `updatedAt`.
   *
   * ⚠️ NOT ADDED, AND EACH IS A DECISION:
   *   · DEACTIVATING an org unit or an officer. ⚠️ FLAGGED AS A FOLLOW-UP rather than settled —
   *     `deactivateOfficer` also sets `access_role` to NONE silently, which is a privilege change
   *     with no trace, and it deserves its own pass rather than a line here.
   *   · archiving, restoring or setting the default policy — each leaves its own trace on the
   *     record (`POST_TYPE` above states the argument).
   *   · `deletePolicyVersion` — the escape hatch for a DRAFT opened by mistake, and a draft is
   *     unreferenced by construction.
   *   · `forkPolicyVersionForArrangement` — it runs inside the ARRANGEMENT's publish transaction,
   *     whose `ARRANGEMENT_PUBLISHED` row is the record of the act; the version it creates carries
   *     `created_by_officer_id` and `origin: 'ARRANGEMENT'`.
   *   · a MOVE for a post type or a policy — neither has a place to move to.
   */
  'ORG_UNIT_CREATED',
  'ORG_UNIT_UPDATED',
  /* ⚠️ A move to the parent it already has writes nothing — the no-op rule above. */
  'ORG_UNIT_REPARENTED',
  'OFFICER_CREATED',
  'OFFICER_UPDATED',
  /*
   * ⚠️ ALWAYS WRITTEN, EVEN WHEN THE UNIT AND RANK DO NOT CHANGE. A transfer to the same posting
   * still closes one `posting_history` row and opens another, which IS a change to the record —
   * the no-op rule is about a write that changes nothing, and this one changes history.
   */
  'OFFICER_TRANSFERRED',
  'POST_TYPE_CREATED',
  'POST_TYPE_UPDATED',
  /* ⚠️ Identity only — name, version one, default, the source of copied roles. CLAIM 4's rule for
     `POLICY_VERSION_PUBLISHED` reaches a create too: never a column or role SHAPE. */
  'POLICY_CREATED',
  'POLICY_UPDATED',
  /*
   * ⚠️ TWO ACTIONS FOR A DRAFT, NOT ONE WITH A FLAG. Opening a draft (from a version, from
   * nothing, or by editing a PUBLISHED version, which forks) creates a document; editing a draft
   * in place overwrites one. "When was this draft started, and from what?" is the question the
   * first answers, and it is asked of a different row than "who changed its columns?".
   *
   * ⚠️ A COMMAND-ROLE CHANGE IS NAMED IN `changed` AND NOTHING MORE. No count, no list — FR-5.2.6b
   * and `commandRoleIsolation.test.ts` CLAIM 4: the roster is compared by a fingerprint, and the
   * row records only that it differs.
   */
  'POLICY_DRAFT_OPENED',
  'POLICY_DRAFT_UPDATED',
  /*
   * ⚠️ [P7B §2a] THE IMPORT — one row for the set and one per template CREATED, none for a template left
   * alone. `ingest:officers`' rule: an audit row per record, and a re-run that changes nothing writes
   * nothing, so three consecutive commits leave `audit_logs` identical too.
   */
  'TEMPLATE_SET_IMPORTED',
  'TEMPLATE_IMPORTED',
  /*
   * ⚠️ [P7B §3a] A DECISION ON A CODE OVERWRITES THE ROW, SO THIS IS THE ONLY RECORD OF WHAT IT WAS.
   * The context carries the class, the code and the previous and new choice as codes and ids — never a
   * name. `DASHBOARD_LAYOUT`'s admission, verbatim: the row keeps who and when, never what.
   */
  'TEMPLATE_DECISION_RECORDED',
  'TEMPLATE_CALENDAR_SET',
  /*
   * ⚠️ [P7B §4d/§4f] ONE ROW WHEN A RUN IS APPROVED. A draft is audited when it PUBLISHES ([P3b.1]), so
   * a CREATE run's drafts write no row each; a PUBLISH run's orders each write their own
   * `ARRANGEMENT_PUBLISHED` through the one publish writer.
   */
  'TEMPLATE_RUN_APPROVED',
  /*
   * ⚠️ [P7B §6b] A RUNNING ORDER TAKEN BACK TO DRAFT BY A MASTER SUPER ADMIN — A DELIBERATE PIERCING OF
   * [C1d]. Written FIRST, before anything is deleted, with the reason and every count the confirmation
   * stated: it is the only record left of the duties, names and markings the act destroys.
   */
  'ARRANGEMENT_REVERTED_TO_DRAFT',
] as const;
export type AuditAction = (typeof AUDIT_ACTIONS)[number];

export const auditLogSchema = z.object({
  id: objectIdSchema,
  entityType: z.enum(AUDIT_ENTITY_TYPES),
  entityId: objectIdSchema,
  action: z.enum(AUDIT_ACTIONS),
  actorOfficerId: objectIdSchema,
  actorOrgUnitId: objectIdSchema,
  /**
   * What changed, as a flat string map.
   *
   * Deliberately NOT an open `unknown`: an audit row is written inside somebody else's
   * transaction and is never validated on the way out, so an unconstrained blob here
   * would be the one place in this codebase where an unchecked shape reaches a client.
   * A merge needs "which place, called what" and nothing more.
   */
  context: z.record(z.string(), z.string()),
  createdAt: z.iso.datetime(),
});

/**
 * ─── [ANALYTICS §8] ONE AUDIT ROW AS THE VIEWER READS IT ───────────────────────────────────
 *
 * The stored row holds ids; this holds names beside them, resolved at READ time behind
 * `AUDIT_ACCESS` ([AUDIT-FLAG]: `audit.read` AND `access_analytics`) —
 * `dutyHistoryEntrySchema`'s rule: "an officer id resolves to a person for anyone
 * entitled to ask; a name in a log line does not un-write".
 *
 * ⚠️ REQUIRED KEYS WITH NULLABLE VALUES. Every row renders a "Who" and an "Entity" cell whatever
 * happened, so `null` answers a question the screen asks — the thing was purged, or the officer
 * row is gone (CLAUDE.md §6 [P6d]'s test). It is never a phone number and never a summary DTO:
 * `officerSummarySchema` carries one, and the viewer has no use for it.
 */
export const auditLogEntrySchema = auditLogSchema.extend({
  actorName: z.string().nullable(),
  actorOrgUnitName: z.string().nullable(),
  entityName: z.string().nullable(),
  /**
   * ⚠️ THE IDS INSIDE `context`, NAMED — keyed by the CONTEXT KEY they were read from. The rows that
   * predate this page store ids there (`ASSIGNMENT_*`'s `before`/`after` officer, a completion's
   * `orgUnitId`, a notification's `addressees`, a disclosure's `deploymentId`), and a hex string on a
   * screen is not data an operator can read.
   *
   * A key is present only when the server knows what its value names AND it held an id; its value is
   * the name (a list's names comma-joined), or `null` when nothing it named still exists. A key that is
   * ABSENT while its value looks like an id was not resolved — a version or place id whose readable
   * twin (`versionNumber`, `mergedPlaceName`) already sits on the same row — and the reader hides it.
   * Required, and `{}` on a row with nothing to name, so a reader never has to ask whether it was sent.
   */
  contextNames: z.record(z.string(), z.string().nullable()),
});

export const AUDIT_LOG_SORT_FIELDS = ['createdAt'] as const;

/**
 * ⚠️ [ANALYTICS §8] THE DEFAULT WINDOW IS THE LAST 90 IST DAYS, AND IT IS A PAGE DEFAULT — NOT
 * RETENTION. `audit_logs` carries no TTL, and no request path, job or migration deletes a row — only
 * the development-only, production-refused `pnpm reset:db` empties it with every other collection
 * (`auditCoverage.test.ts` names that file in an exact list). A caller who names an earlier
 * `dateFrom` reads it. The window exists so the unfiltered first page is an index range rather
 * than the whole collection, and so an exact `countDocuments` beside it stays bounded.
 *
 * It is the SERVER's default, `INBOX_DEFAULT_WINDOW_DAYS`' rule: a date written into the URL on
 * mount would bake today into every shared link.
 */
export const AUDIT_DEFAULT_WINDOW_DAYS = 90;

export const listAuditLogQuerySchema = createPaginationSchema(AUDIT_LOG_SORT_FIELDS, 'createdAt')
  .extend({
    entityType: z.enum(AUDIT_ENTITY_TYPES).optional(),
    /** Only meaningful with `entityType` — the index is compound and leads with it. REFUSED without it, below. */
    entityId: objectIdSchema.optional(),
    actorOfficerId: objectIdSchema.optional(),
    /*
     * ⚠️ COMMA-JOINED OR REPEATED, `dutyCountsQuerySchema.officerIds`' preprocess. The browser client's
     * `query` option takes SCALARS (`buildUrl` uses `params.set`), so several actions travel as one
     * comma-joined value — unambiguous, because no member of `AUDIT_ACTIONS` contains a comma.
     */
    action: z.preprocess(
      (value) => (typeof value === 'string' ? value.split(',').filter(Boolean) : value),
      z.array(z.enum(AUDIT_ACTIONS)).min(1).optional(),
    ),
    /** IST calendar dates, both inclusive. See `AUDIT_DEFAULT_WINDOW_DAYS` for what happens when omitted. */
    dateFrom: calendarDateSchema.optional(),
    dateTo: calendarDateSchema.optional(),
  })
  /*
   * ⚠️ REFUSED RATHER THAN IGNORED. An `entityId` with no type would be a filter the compound index
   * cannot lead with — a scan — and silently dropping it would answer a question nobody asked. The
   * middleware STRIPS unknown keys; a known key it cannot honour is a 422 naming the path.
   */
  .refine((query) => query.entityId === undefined || query.entityType !== undefined, {
    message: 'An entity id needs its entity type',
    path: ['entityId'],
  })
  /* `YYYY-MM-DD` strings order lexicographically as they do chronologically (CLAUDE.md §7 [P3a]). */
  .refine((query) => query.dateFrom === undefined || query.dateTo === undefined || query.dateFrom <= query.dateTo, {
    message: 'The start date is after the end date',
    path: ['dateTo'],
  });
